import React, { useEffect, useRef, useState } from "react";
import { OwlMascot } from "@/components/OwlMascot";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Mic, Send, Volume2, VolumeX, Loader2, StopCircle } from "lucide-react";
import { api, API } from "@/lib/api";
import { toast } from "sonner";

const MODELS = [
  { key: "anthropic:claude-sonnet-4-5-20250929", label: "Claude Sonnet 4.5", provider: "anthropic", model: "claude-sonnet-4-5-20250929" },
  { key: "openai:gpt-5.2", label: "GPT 5.2", provider: "openai", model: "gpt-5.2" },
  { key: "gemini:gemini-3-flash-preview", label: "Gemini 3 Flash", provider: "gemini", model: "gemini-3-flash-preview" },
];

const VOICES = [
  { key: "shimmer", label: "Shimmer · bright & cheerful" },
  { key: "nova", label: "Nova · energetic" },
  { key: "coral", label: "Coral · warm friendly" },
  { key: "sage", label: "Sage · wise & calm" },
  { key: "alloy", label: "Alloy · neutral" },
  { key: "echo", label: "Echo · smooth" },
  { key: "fable", label: "Fable · storyteller" },
  { key: "onyx", label: "Onyx · deep" },
  { key: "ash", label: "Ash · articulate" },
];

const AITutorPanel = ({ experimentId, experiment, currentUser }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [selected, setSelected] = useState(MODELS[0].key);
  const [ttsVoice, setTtsVoice] = useState(() => localStorage.getItem("brimi_tts_voice") || "shimmer");
  const [streaming, setStreaming] = useState(false);
  const [recording, setRecording] = useState(false);
  const [audioOn, setAudioOn] = useState(true);
  const [ttsBusy, setTtsBusy] = useState(false);
  const [talking, setTalking] = useState(false);
  const bottomRef = useRef(null);
  const abortRef = useRef(null);
  const mediaRef = useRef(null);
  const audioRef = useRef(null);
  const audioSessionRef = useRef(0);
  const audioMountedRef = useRef(true);
  // Stable session id per (user, experiment) — persists chat history across mounts.
  const sessionIdRef = useRef(`chat_${currentUser?.user_id || "anon"}_${experimentId}`);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    // Load persisted history from backend for this session
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/ai/history", { params: { session_id: sessionIdRef.current } });
        if (cancelled) return;
        if (data && data.length) {
          setMessages(data.map((m) => ({ role: m.role, content: m.content })));
        } else {
          setMessages([{
            role: "assistant",
            content: `Hi! I'm Brimi 🦉 — ready to help with "${experiment.title}". Ask me anything, or just start experimenting and I'll chime in!`
          }]);
        }
      } catch {
        setMessages([{
          role: "assistant",
          content: `Hi! I'm Brimi 🦉 — ready to help with "${experiment.title}".`
        }]);
      }
    })();
    return () => { cancelled = true; };
  }, [experiment.id, experiment.title]);

  const sendMessage = async (text) => {
    const trimmed = text.trim();
    if (!trimmed || streaming) return;
    const model = MODELS.find((m) => m.key === selected);

    setMessages((m) => [...m, { role: "user", content: trimmed }, { role: "assistant", content: "" }]);
    setInput("");
    setStreaming(true);

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const token = localStorage.getItem("brimi_token");
      const res = await fetch(`${API}/ai/chat`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          message: trimmed,
          experiment_id: experimentId,
          session_id: sessionIdRef.current,
          provider: model.provider,
          model: model.model,
        }),
        signal: controller.signal,
      });

      if (!res.ok || !res.body) {
        throw new Error("Chat failed");
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let full = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value, { stream: true });
        for (const line of chunk.split("\n")) {
          if (!line.startsWith("data: ")) continue;
          try {
            const evt = JSON.parse(line.slice(6));
            if (evt.delta) {
              full += evt.delta;
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", content: full };
                return copy;
              });
            }
            if (evt.error) {
              toast.error(evt.error);
            }
          } catch (_e) { /* ignore malformed SSE line */ }
        }
      }
      if (audioOn && full) speakText(full);
    } catch (err) {
      if (err.name !== "AbortError") {
        toast.error("Brimi lost signal. Try again.");
      }
    } finally {
      setStreaming(false);
    }
  };

  const speakText = async (text) => {
    // Session guard — increment on every new speak so late TTS blobs are discarded.
    audioSessionRef.current += 1;
    const mySession = audioSessionRef.current;
    // Stop any currently-playing audio (from previous request).
    try {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.onended = null;
        audioRef.current.onerror = null;
        audioRef.current.src = "";
      }
    } catch (_e) { /* ignore */ }
    audioRef.current = null;
    setTalking(false);

    try {
      setTtsBusy(true);
      const clean = text.replace(/[*_#`>]/g, "").slice(0, 3500);
      const res = await api.post("/ai/tts", { text: clean, voice: ttsVoice }, { responseType: "blob" });
      // Discard if session changed or component unmounted.
      if (!audioMountedRef.current || audioSessionRef.current !== mySession) return;
      const url = URL.createObjectURL(res.data);
      const audio = new Audio(url);
      audioRef.current = audio;
      audio.onplay = () => { if (audioMountedRef.current && audioSessionRef.current === mySession) setTalking(true); };
      audio.onended = () => { if (audioMountedRef.current) { setTalking(false); URL.revokeObjectURL(url); } };
      audio.onerror = () => { if (audioMountedRef.current) setTalking(false); };
      await audio.play();
    } catch (err) {
      console.warn("TTS failed:", err?.message || err);
    } finally {
      if (audioMountedRef.current) setTtsBusy(false);
    }
  };

  const stopSpeaking = () => {
    audioSessionRef.current += 1; // invalidate any in-flight TTS
    try {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.onended = null;
        audioRef.current.src = "";
      }
    } catch (_e) { /* ignore */ }
    audioRef.current = null;
    setTalking(false);
  };

  // Cleanup on unmount so late TTS never spawns audio after we're gone.
  useEffect(() => {
    return () => {
      audioMountedRef.current = false;
      audioSessionRef.current += 1;
      try { audioRef.current?.pause(); if (audioRef.current) audioRef.current.src = ""; } catch (_e) { /* ignore */ }
      audioRef.current = null;
    };
  }, []);

  const toggleRecord = async () => {
    if (recording) {
      mediaRef.current?.stop();
      setRecording(false);
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream, { mimeType: "audio/webm" });
      const chunks = [];
      rec.ondataavailable = (e) => chunks.push(e.data);
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunks, { type: "audio/webm" });
        const form = new FormData();
        form.append("file", blob, "voice.webm");
        try {
          const { data } = await api.post("/ai/stt", form);
          if (data.text) {
            setInput(data.text);
            sendMessage(data.text);
          } else {
            toast("Didn't catch that. Try again?");
          }
        } catch (err) {
          console.error("STT failed:", err);
          toast.error("Voice transcription failed.");
        }
      };
      rec.start();
      mediaRef.current = rec;
      setRecording(true);
    } catch (err) {
      console.error("Mic access denied or unavailable:", err);
      toast.error("Please allow microphone access.");
    }
  };

  return (
    <aside className="w-full lg:w-80 shrink-0 flex flex-col max-h-[40vh] lg:max-h-none lg:h-full border-b lg:border-b-0 lg:border-r border-slate-800 bg-slate-900/60 backdrop-blur-xl overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-slate-800">
        <div className="flex items-center gap-3">
          <div className="relative">
            <div className={talking ? "anim-pulse-glow rounded-full" : ""}>
              <OwlMascot size={54} talking={talking || streaming} />
            </div>
          </div>
          <div className="flex-1">
            <div className="font-outfit font-bold text-slate-100 leading-tight">Brimi</div>
            <div className="text-xs text-cyan-400 font-mono">
              {streaming ? "thinking…" : talking ? "speaking…" : "AI tutor · online"}
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={() => setAudioOn(!audioOn)} data-testid="tutor-audio-toggle" aria-label="Toggle audio">
            {audioOn ? <Volume2 className="h-4 w-4 text-cyan-300" /> : <VolumeX className="h-4 w-4 text-slate-500" />}
          </Button>
        </div>
        <Select value={selected} onValueChange={setSelected}>
          <SelectTrigger className="mt-3 bg-slate-800/80 border-slate-700 text-slate-100 h-9 text-xs" data-testid="tutor-model-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {MODELS.map((m) => <SelectItem key={m.key} value={m.key}>{m.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select
          value={ttsVoice}
          onValueChange={(v) => { setTtsVoice(v); localStorage.setItem("brimi_tts_voice", v); }}
        >
          <SelectTrigger className="mt-2 bg-slate-800/80 border-slate-700 text-slate-100 h-9 text-xs" data-testid="tutor-voice-select">
            <SelectValue placeholder="Voice" />
          </SelectTrigger>
          <SelectContent>
            {VOICES.map((v) => <SelectItem key={v.key} value={v.key}>{v.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
              m.role === "user"
                ? "bg-cyan-500/20 text-cyan-50 rounded-tr-sm border border-cyan-500/30"
                : "bg-slate-800/80 text-slate-200 rounded-tl-sm border border-slate-700"
            }`} data-testid={`tutor-msg-${m.role}-${i}`}>
              {m.content || <span className="inline-flex gap-1"><span className="wave-bar" /><span className="wave-bar" /><span className="wave-bar" /></span>}
            </div>
          </div>
        ))}
        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <div className="p-3 border-t border-slate-800">
        <div className="flex items-end gap-2">
          <Textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(input); } }}
            placeholder={recording ? "Listening…" : "Ask Brimi anything…"}
            rows={2}
            className="bg-slate-800/80 border-slate-700 text-slate-100 placeholder:text-slate-500 resize-none rounded-2xl text-sm"
            disabled={streaming || recording}
            data-testid="tutor-input"
          />
          <div className="flex flex-col gap-2">
            <Button
              type="button"
              size="icon"
              onClick={toggleRecord}
              className={`rounded-full ${recording ? "bg-rose-500 hover:bg-rose-600 anim-pulse-glow" : "bg-slate-800 hover:bg-slate-700"}`}
              data-testid="tutor-mic-btn"
              aria-label="Record voice"
            >
              {recording ? <StopCircle className="h-4 w-4" /> : <Mic className="h-4 w-4 text-cyan-300" />}
            </Button>
            <Button
              type="button"
              size="icon"
              onClick={() => sendMessage(input)}
              className="rounded-full bg-cyan-500 hover:bg-cyan-400 text-slate-900"
              disabled={streaming || !input.trim()}
              data-testid="tutor-send-btn"
              aria-label="Send"
            >
              {streaming ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </Button>
          </div>
        </div>
        {talking && (
          <button type="button" onClick={stopSpeaking} className="mt-2 text-xs text-cyan-400 font-mono hover:underline" data-testid="tutor-stop-speaking">
            Stop speaking
          </button>
        )}
      </div>
    </aside>
  );
};

export default AITutorPanel;
